import React, { useState, useEffect, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Product, PricingItem } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea, FormCheckbox } from '../components/FormInput';
import { useApi } from '../../../api/useApi';

interface SetupProductsProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const categoryOptions = [
  { value: 'court-filing', label: 'Court Filing' },
  { value: 'process-serving', label: 'Process Serving' },
  { value: 'document-retrieval', label: 'Document Retrieval' },
  { value: 'eservice', label: 'E-Service' },
  { value: 'skip-trace', label: 'Skip Trace' },
  { value: 'courier', label: 'Courier' },
  { value: 'research', label: 'Research' },
  { value: 'other', label: 'Other' },
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'discontinued', label: 'Discontinued' },
];

const pricingTypeOptions = [
  { value: 'flat', label: 'Flat Rate' },
  { value: 'per-page', label: 'Per Page' },
  { value: 'per-attempt', label: 'Per Attempt' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'tiered', label: 'Tiered' },
  { value: 'variable', label: 'Variable' },
];

const SetupProducts: React.FC<SetupProductsProps> = ({ brand, Icons }) => {
  const { get, post, patch } = useApi();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<Product>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set());

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);

      const data = await get<{ data: Product[] }>(`/api/admin/setup/products?${params}`);
      setProducts(data.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get, search, statusFilter, categoryFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchProducts();
  }, [fetchProducts]);

  const handleRowClick = (product: Product) => {
    setSelectedProduct(product);
    setFormData(product);
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setFormData({
      code: '',
      name: '',
      category: 'court-filing',
      status: 'active',
      pricingItems: [],
      requiresDocuments: true,
      requiresPartyInfo: false,
      requiresAddress: false,
      vendorDispatch: false,
      sameDayAvailable: false,
      featured: false,
    });
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePricingItemChange = (index: number, field: keyof PricingItem, value: unknown) => {
    setFormData(prev => {
      const items = [...(prev.pricingItems || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, pricingItems: items };
    });
  };

  const handleAddPricingItem = () => {
    setFormData(prev => ({
      ...prev,
      pricingItems: [
        ...(prev.pricingItems || []),
        {
          id: crypto.randomUUID(),
          name: '',
          pricingType: 'flat' as const,
          basePrice: 0,
          rushMultiplier: 1.5,
          sameDayMultiplier: 2.0,
          taxable: false,
          optional: false,
          sortOrder: (prev.pricingItems?.length || 0) + 1,
        },
      ],
    }));
  };

  const handleRemovePricingItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      pricingItems: prev.pricingItems?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isCreating) {
        await post('/api/admin/setup/products', formData);
      } else {
        await patch(`/api/admin/setup/products/${selectedProduct?.id}`, formData);
      }

      setIsDrawerOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const columns = [
    {
      key: 'expand',
      header: '',
      width: '40px',
      render: (product: Product) => (
        product.pricingItems.length > 0 ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(product.id);
            }}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <Icons.ChevronRight
              className={`w-4 h-4 transition-transform ${
                expandedItems.has(product.id) ? 'rotate-90' : ''
              }`}
              style={{ color: brand.colors.textLight }}
            />
          </button>
        ) : <div className="w-6" />
      ),
    },
    {
      key: 'code',
      header: 'Code',
      width: '120px',
      render: (product: Product) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: brand.colors.link }}>
            {product.code}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={product.name}
            fields={[
              { label: 'Code', value: product.code },
              { label: 'Category', value: product.category.replace('-', ' ') },
              { label: 'Base Price', value: product.basePrice ? `$${product.basePrice.toFixed(2)}` : '-' },
              { label: 'Turnaround', value: product.standardTurnaround ? `${product.standardTurnaround}h` : '-' },
              { label: 'Same Day', value: product.sameDayAvailable ? 'Yes' : 'No' },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (product: Product) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>
            {product.name}
            {product.featured && (
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                Featured
              </span>
            )}
          </div>
          {product.description && (
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {product.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (product: Product) => (
        <span className="capitalize">{product.category.replace('-', ' ')}</span>
      ),
    },
    {
      key: 'basePrice',
      header: 'Base Price',
      render: (product: Product) => (
        <span>{formatPrice(product.basePrice)}</span>
      ),
    },
    {
      key: 'pricingItems',
      header: 'Pricing Items',
      render: (product: Product) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {product.pricingItems.length} items
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (product: Product) => <StatusBadge status={product.status} />,
    },
  ];

  return (
    <div>
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search products..."
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusChange={setStatusFilter}
        categoryFilter={categoryFilter}
        categoryOptions={categoryOptions}
        onCategoryChange={setCategoryFilter}
        onAdd={handleAdd}
        addLabel="Add Product"
      />

      {/* Custom table with expandable rows */}
      <div
        className="bg-white rounded-lg border overflow-hidden"
        style={{ borderColor: brand.colors.border }}
      >
        {isLoading ? (
          <div className="p-8 text-center">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto"
              style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
            />
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              No products found
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: brand.colors.bgLight }}>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: brand.colors.textLight, width: col.width }}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: brand.colors.border }}>
                {products.map((product) => (
                  <React.Fragment key={product.id}>
                    <tr
                      onClick={() => handleRowClick(product)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className="px-4 py-3 text-sm"
                          style={{ color: brand.colors.text }}
                        >
                          {col.render ? col.render(product) : (product as Record<string, unknown>)[col.key] as string}
                        </td>
                      ))}
                    </tr>
                    {/* Expanded pricing items */}
                    {expandedItems.has(product.id) && product.pricingItems.length > 0 && (
                      <tr>
                        <td colSpan={columns.length} className="p-0">
                          <div
                            className="px-8 py-4"
                            style={{ backgroundColor: brand.colors.bgLight }}
                          >
                            <table className="w-full">
                              <thead>
                                <tr>
                                  <th className="text-left text-xs font-medium uppercase px-3 py-2" style={{ color: brand.colors.textLight }}>Item</th>
                                  <th className="text-left text-xs font-medium uppercase px-3 py-2" style={{ color: brand.colors.textLight }}>Type</th>
                                  <th className="text-right text-xs font-medium uppercase px-3 py-2" style={{ color: brand.colors.textLight }}>Base Price</th>
                                  <th className="text-right text-xs font-medium uppercase px-3 py-2" style={{ color: brand.colors.textLight }}>Unit Price</th>
                                  <th className="text-center text-xs font-medium uppercase px-3 py-2" style={{ color: brand.colors.textLight }}>Optional</th>
                                </tr>
                              </thead>
                              <tbody>
                                {product.pricingItems.map((item) => (
                                  <tr key={item.id}>
                                    <td className="px-3 py-2 text-sm" style={{ color: brand.colors.text }}>{item.name}</td>
                                    <td className="px-3 py-2 text-sm capitalize" style={{ color: brand.colors.textLight }}>{item.pricingType.replace('-', ' ')}</td>
                                    <td className="px-3 py-2 text-sm text-right" style={{ color: brand.colors.text }}>{formatPrice(item.basePrice)}</td>
                                    <td className="px-3 py-2 text-sm text-right" style={{ color: brand.colors.text }}>{item.unitPrice ? formatPrice(item.unitPrice) : '-'}</td>
                                    <td className="px-3 py-2 text-sm text-center">{item.optional ? 'Yes' : 'No'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'Add Product' : 'Edit Product'}
        subtitle={selectedProduct?.code}
        brand={brand}
        Icons={Icons}
        width="xl"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <FormInput
              brand={brand}
              label="Code"
              name="code"
              value={formData.code || ''}
              onChange={handleInputChange}
              required
              placeholder="e.g., CF-STD"
            />
            <FormSelect
              brand={brand}
              label="Category"
              name="category"
              value={formData.category || 'court-filing'}
              onChange={handleInputChange}
              options={categoryOptions}
            />
            <FormSelect
              brand={brand}
              label="Status"
              name="status"
              value={formData.status || 'active'}
              onChange={handleInputChange}
              options={statusOptions}
            />
          </div>

          <FormInput
            brand={brand}
            label="Name"
            name="name"
            value={formData.name || ''}
            onChange={handleInputChange}
            required
          />

          <FormTextarea
            brand={brand}
            label="Description"
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            rows={2}
          />

          <div className="grid grid-cols-3 gap-4">
            <FormInput
              brand={brand}
              label="Base Price ($)"
              name="basePrice"
              type="number"
              value={formData.basePrice || ''}
              onChange={handleInputChange}
            />
            <FormInput
              brand={brand}
              label="Standard Turnaround (hrs)"
              name="standardTurnaround"
              type="number"
              value={formData.standardTurnaround || ''}
              onChange={handleInputChange}
            />
            <FormInput
              brand={brand}
              label="Rush Turnaround (hrs)"
              name="rushTurnaround"
              type="number"
              value={formData.rushTurnaround || ''}
              onChange={handleInputChange}
            />
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium" style={{ color: brand.colors.text }}>
                Pricing Items
              </h3>
              <button
                onClick={handleAddPricingItem}
                className="flex items-center gap-1 text-sm font-medium"
                style={{ color: brand.colors.link }}
              >
                <Icons.Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            {formData.pricingItems?.length === 0 ? (
              <div
                className="text-center py-6 border rounded-lg"
                style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
              >
                No pricing items. Click "Add Item" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.pricingItems?.map((item, index) => (
                  <div
                    key={item.id}
                    className="p-4 border rounded-lg"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
                        Item {index + 1}
                      </span>
                      <button
                        onClick={() => handleRemovePricingItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Icons.X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label htmlFor={`product-name-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Name</label>
                        <input
                          id={`product-name-${index}`}
                          type="text"
                          value={item.name}
                          onChange={(e) => handlePricingItemChange(index, 'name', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                        />
                      </div>
                      <div>
                        <label htmlFor={`product-type-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Type</label>
                        <select
                          id={`product-type-${index}`}
                          value={item.pricingType}
                          onChange={(e) => handlePricingItemChange(index, 'pricingType', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                        >
                          {pricingTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`product-base-price-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Base Price</label>
                        <input
                          id={`product-base-price-${index}`}
                          type="number"
                          value={item.basePrice}
                          onChange={(e) => handlePricingItemChange(index, 'basePrice', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <div>
                        <label htmlFor={`product-unit-price-${index}`} className="block text-xs mb-1" style={{ color: brand.colors.textLight }}>Unit Price</label>
                        <input
                          id={`product-unit-price-${index}`}
                          type="number"
                          value={item.unitPrice || ''}
                          onChange={(e) => handlePricingItemChange(index, 'unitPrice', parseFloat(e.target.value) || undefined)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                          style={{ borderColor: brand.colors.border }}
                        />
                      </div>
                      <div className="flex items-center gap-4 pt-5">
                        <label className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={item.optional}
                            onChange={(e) => handlePricingItemChange(index, 'optional', e.target.checked)}
                            style={{ accentColor: brand.colors.primary }}
                          />
                          Optional
                        </label>
                        <label className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={item.taxable}
                            onChange={(e) => handlePricingItemChange(index, 'taxable', e.target.checked)}
                            style={{ accentColor: brand.colors.primary }}
                          />
                          Taxable
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
              Requirements
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <FormCheckbox
                brand={brand}
                label="Requires Documents"
                name="requiresDocuments"
                checked={formData.requiresDocuments || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Requires Party Info"
                name="requiresPartyInfo"
                checked={formData.requiresPartyInfo || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Requires Address"
                name="requiresAddress"
                checked={formData.requiresAddress || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Vendor Dispatch"
                name="vendorDispatch"
                checked={formData.vendorDispatch || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Same Day Available"
                name="sameDayAvailable"
                checked={formData.sameDayAvailable || false}
                onChange={handleInputChange}
              />
              <FormCheckbox
                brand={brand}
                label="Featured Product"
                name="featured"
                checked={formData.featured || false}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div
            className="border-t pt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <FormTextarea
              brand={brand}
              label="Notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupProducts;
